import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  AlertCircle,
  BarChart3,
  Bell,
  Check,
  Mail,
  RefreshCw,
  Share2,
  ShieldCheck,
} from 'lucide-react';

import { FormattedMessage } from '../../util/reactIntl';
import { hollowIconDefaults, iconSize } from '../../util/iconSettings';
import {
  fetchConsumerConsents,
  isAmbiguousRematchError,
  updateConsumerConsent,
} from '../../util/rematchApi';
import IconSpinner from '../IconSpinner/IconSpinner';

import css from './DataProcessingConsents.module.css';

const OPTIONAL_PURPOSES = [
  'ANALYTICS',
  'MARKETING_EMAIL',
  'REWARD_NOTIFICATIONS',
  'SOCIAL_SHARING',
];

const CONSENT_PURPOSES = ['ESSENTIAL', ...OPTIONAL_PURPOSES];

const PURPOSE_ICONS = {
  ESSENTIAL: ShieldCheck,
  ANALYTICS: BarChart3,
  MARKETING_EMAIL: Mail,
  REWARD_NOTIFICATIONS: Bell,
  SOCIAL_SHARING: Share2,
};

const stateFromConsents = consents => {
  const byPurpose = Array.isArray(consents)
    ? consents.reduce((acc, item) => {
        if (item?.purpose) {
          acc[item.purpose] = !!item.granted;
        }
        return acc;
      }, {})
    : {};

  return CONSENT_PURPOSES.reduce((acc, purpose) => {
    acc[purpose] = purpose === 'ESSENTIAL' ? true : !!byPurpose[purpose];
    return acc;
  }, {});
};

const grantedFromResponse = (consents, purpose, fallback) => {
  const savedConsent = Array.isArray(consents)
    ? consents.find(consent => consent?.purpose === purpose)
    : consents?.purpose === purpose
    ? consents
    : null;

  return savedConsent ? !!savedConsent.granted : fallback;
};

const hasExpectedGrant = (consents, purpose, expectedGrant) => {
  const savedConsent = Array.isArray(consents)
    ? consents.find(consent => consent?.purpose === purpose)
    : consents?.purpose === purpose
    ? consents
    : null;

  return savedConsent ? !!savedConsent.granted === expectedGrant : false;
};

const PurposeIcon = props => {
  const { purpose } = props;
  const Icon = PURPOSE_ICONS[purpose] || ShieldCheck;
  return <Icon size={iconSize.topbar} {...hollowIconDefaults} aria-hidden="true" />;
};

const Toggle = props => {
  const { checked, disabled, labelledBy, onChange } = props;

  return (
    <button
      className={classNames(css.toggle, {
        [css.toggleChecked]: checked,
        [css.toggleDisabled]: disabled,
      })}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={onChange}
    >
      <span className={css.toggleTrack}>
        <span className={css.toggleThumb} />
      </span>
    </button>
  );
};

const ConsentRow = props => {
  const { checked, disabled, onToggle, purpose, required } = props;
  const labelId = `DataProcessingConsents.${purpose}.title`;
  const titleDomId = `data-processing-consent-${purpose}`;

  return (
    <li className={classNames(css.consentRow, { [css.requiredRow]: required })}>
      <span className={css.iconTile}>
        <PurposeIcon purpose={purpose} />
      </span>
      <span className={css.consentCopy}>
        <span className={css.consentTitle} id={titleDomId}>
          <FormattedMessage id={labelId} />
        </span>
        <span className={css.consentText}>
          <FormattedMessage id={`DataProcessingConsents.${purpose}.body`} />
        </span>
      </span>
      {required ? (
        <span className={css.requiredBadge}>
          <Check size={iconSize.tiny} {...hollowIconDefaults} aria-hidden="true" />
          <FormattedMessage id="DataProcessingConsents.required" />
        </span>
      ) : (
        <Toggle checked={checked} disabled={disabled} labelledBy={titleDomId} onChange={onToggle} />
      )}
    </li>
  );
};

const SaveStatus = props => {
  const { error } = props;

  if (error) {
    return (
      <p className={classNames(css.statusMessage, css.errorMessage)}>
        <AlertCircle size={iconSize.compact} {...hollowIconDefaults} aria-hidden="true" />
        <FormattedMessage id="DataProcessingConsents.error" />
      </p>
    );
  }

  return null;
};

const DataProcessingConsents = props => {
  const {
    className,
    rootClassName,
    onCompleted,
    showCard = true,
    source = 'SETTINGS',
    variant = 'settings',
  } = props;
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [fetchInProgress, setFetchInProgress] = useState(false);
  const [saveAllInProgress, setSaveAllInProgress] = useState(false);
  const [pendingPurposes, setPendingPurposes] = useState([]);
  const [error, setError] = useState(null);

  const isPrompt = variant === 'prompt';
  const rootClasses = classNames(rootClassName || (showCard ? css.rootCard : css.root), className);
  const values = draft || stateFromConsents([]);

  const saveConsent = async ({ purpose, granted }) => {
    try {
      return await updateConsumerConsent({ purpose, granted, source });
    } catch (error) {
      if (isAmbiguousRematchError(error)) {
        try {
          const consents = await fetchConsumerConsents();
          if (hasExpectedGrant(consents, purpose, granted)) {
            return consents;
          }
        } catch (_reconciliationError) {
          // Keep the write error because it describes the uncertain operation.
        }
      }
      throw error;
    }
  };

  const loadConsents = useCallback(async (options = {}) => {
    const { signal } = options;
    setFetchInProgress(true);
    setError(null);

    try {
      const consents = await fetchConsumerConsents(options);
      if (signal?.aborted) {
        return null;
      }

      const next = stateFromConsents(consents);
      setBaseline(next);
      setDraft(next);
      return consents;
    } catch (e) {
      if (!signal?.aborted) {
        setError(e);
      }
      return null;
    } finally {
      if (!signal?.aborted) {
        setFetchInProgress(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    void loadConsents({ signal: controller?.signal });
    return () => controller?.abort();
  }, [loadConsents]);

  const changedPurposes = useMemo(() => {
    if (!baseline || !draft) {
      return [];
    }

    return OPTIONAL_PURPOSES.filter(purpose => baseline[purpose] !== draft[purpose]);
  }, [baseline, draft]);

  const handleSettingsToggle = async purpose => {
    if (pendingPurposes.includes(purpose)) {
      return null;
    }

    const nextGranted = !values[purpose];
    const previousGranted = !!values[purpose];
    const optimisticDraft = {
      ...values,
      [purpose]: nextGranted,
    };

    setDraft(optimisticDraft);
    setPendingPurposes(current => [...current, purpose]);
    setError(null);

    try {
      const consents = await saveConsent({ purpose, granted: nextGranted });
      const savedGranted = grantedFromResponse(consents, purpose, nextGranted);

      setBaseline(current => ({
        ...(current || stateFromConsents([])),
        [purpose]: savedGranted,
      }));
      setDraft(current => ({
        ...(current || stateFromConsents([])),
        [purpose]: savedGranted,
      }));
      return consents;
    } catch (e) {
      setDraft(current => ({
        ...(current || stateFromConsents([])),
        [purpose]: previousGranted,
      }));
      setError(e);
      return null;
    } finally {
      setPendingPurposes(current => current.filter(item => item !== purpose));
    }
  };

  const handlePromptToggle = purpose => {
    setDraft(current => ({
      ...(current || stateFromConsents([])),
      [purpose]: !values[purpose],
    }));
  };

  const handleSaveAll = async () => {
    if (!baseline || !draft) {
      return null;
    }

    setSaveAllInProgress(true);
    setError(null);
    let confirmedDraft = { ...draft };

    try {
      for (const purpose of changedPurposes) {
        const consents = await saveConsent({
          purpose,
          granted: draft[purpose],
        });
        const savedGranted = grantedFromResponse(consents, purpose, draft[purpose]);
        confirmedDraft = { ...confirmedDraft, [purpose]: savedGranted };
        setBaseline(current => ({
          ...(current || stateFromConsents([])),
          [purpose]: savedGranted,
        }));
        setDraft(current => ({
          ...(current || stateFromConsents([])),
          [purpose]: savedGranted,
        }));
      }

      setBaseline(confirmedDraft);
      setDraft(confirmedDraft);
      onCompleted?.();
      return confirmedDraft;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setSaveAllInProgress(false);
    }
  };

  const handleKeepDefaults = () => {
    if (onCompleted) {
      onCompleted();
    }
  };

  const titleId = isPrompt ? 'DataProcessingConsents.promptTitle' : 'DataProcessingConsents.title';
  const bodyId = isPrompt ? 'DataProcessingConsents.promptBody' : 'DataProcessingConsents.body';
  const initialLoadFailed = !fetchInProgress && !draft && !!error;

  return (
    <section className={rootClasses}>
      <div className={css.header}>
        <div className={css.headerIcon}>
          <ShieldCheck size={iconSize.dashboardEmpty} {...hollowIconDefaults} aria-hidden="true" />
        </div>
        <div className={css.headerCopy}>
          <h2 className={css.title}>
            <FormattedMessage id={titleId} />
          </h2>
          <p className={css.body}>
            <FormattedMessage id={bodyId} />
          </p>
        </div>
      </div>

      {fetchInProgress ? (
        <div className={css.loading}>
          <IconSpinner />
        </div>
      ) : initialLoadFailed ? (
        <div className={css.loadError} role="alert">
          <AlertCircle size={iconSize.dashboardEmpty} {...hollowIconDefaults} aria-hidden="true" />
          <div className={css.loadErrorCopy}>
            <p className={css.loadErrorTitle}>
              <FormattedMessage id="DataProcessingConsents.loadErrorTitle" />
            </p>
            <p className={css.loadErrorBody}>
              <FormattedMessage id="DataProcessingConsents.loadErrorBody" />
            </p>
          </div>
          <button className={css.retryButton} type="button" onClick={() => void loadConsents()}>
            <RefreshCw size={iconSize.compact} {...hollowIconDefaults} aria-hidden="true" />
            <FormattedMessage id="DataProcessingConsents.retry" />
          </button>
        </div>
      ) : (
        <>
          <ul className={css.consentList}>
            {CONSENT_PURPOSES.map(purpose => {
              const required = purpose === 'ESSENTIAL';
              const disabled =
                required || pendingPurposes.includes(purpose) || (isPrompt && saveAllInProgress);
              return (
                <ConsentRow
                  key={purpose}
                  purpose={purpose}
                  checked={values[purpose]}
                  disabled={disabled}
                  required={required}
                  onToggle={() =>
                    required
                      ? null
                      : isPrompt
                      ? handlePromptToggle(purpose)
                      : void handleSettingsToggle(purpose)
                  }
                />
              );
            })}
          </ul>
          <SaveStatus error={error} />
          {isPrompt ? (
            <div className={css.actions}>
              <button
                className={css.secondaryAction}
                type="button"
                disabled={saveAllInProgress}
                onClick={handleKeepDefaults}
              >
                <FormattedMessage id="DataProcessingConsents.keepDefaults" />
              </button>
              <button
                className={css.primaryAction}
                type="button"
                disabled={saveAllInProgress}
                onClick={() => void handleSaveAll()}
              >
                {saveAllInProgress ? (
                  <IconSpinner rootClassName={css.actionSpinner} />
                ) : (
                  <FormattedMessage id="DataProcessingConsents.savePreferences" />
                )}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default DataProcessingConsents;
